import { ChangeDetectionStrategy, Component, inject, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '@mm-aggregator/drf-dynamics';
import { map, tap } from 'rxjs';

import { UI, createServerSearch } from '../ui';
import type { CountryAccessValue } from './merchant-config.models';

export interface CountryOption {
  id: string;
  code: string;
  name: string;
}

interface CountryRow {
  id: string | number;
  code?: string;
  name: string;
}

/**
 * Per-country access: two server-searched multi-selects, allowed and disallowed (leave a country out of both for
 * no restriction). A country is in at most one list, which is the rule the API enforces. Countries are searched
 * on the API (`?search=`, at most 20 per request); every country seen is reported through `countries` so the
 * parent can show names.
 */
@Component({
  selector: 'mm-country-access-config',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ...UI],
  template: `
    <p class="mb-3 text-[0.8125rem] text-muted">
      Leave both lists empty for no restriction. With any country allowed, only allowed countries
      are open; disallowed countries are always refused.
    </p>
    <div class="grid gap-4 md:grid-cols-2">
      <ui-form-field label="Allowed countries" [hint]="value().allowed.length + ' allowed'">
        <ui-multi-autocomplete
          placeholder="Search countries…"
          ariaLabel="Allowed countries"
          [options]="allowedSearch.options()"
          [loading]="allowedSearch.loading()"
          [filterLocal]="false"
          [disabled]="disabled()"
          [ngModel]="value().allowed"
          (ngModelChange)="setAllowed($event)"
          (search)="allowedSearch.search($event)"
        />
      </ui-form-field>
      <ui-form-field label="Disallowed countries" [hint]="value().disallowed.length + ' disallowed'">
        <ui-multi-autocomplete
          placeholder="Search countries…"
          ariaLabel="Disallowed countries"
          [options]="disallowedSearch.options()"
          [loading]="disallowedSearch.loading()"
          [filterLocal]="false"
          [disabled]="disabled()"
          [ngModel]="value().disallowed"
          (ngModelChange)="setDisallowed($event)"
          (search)="disallowedSearch.search($event)"
        />
      </ui-form-field>
    </div>
  `,
})
export class CountryAccessConfigComponent {
  private readonly api = inject(ApiService);

  readonly value = model.required<CountryAccessValue>();
  /** Countries seen so far (id, code, name); read by the parent to label ids. */
  readonly countries = model<readonly CountryOption[]>([]);
  readonly disabled = input(false);

  private readonly fetch = (term: string) =>
    this.api
      .get<{ results?: CountryRow[] }>('finances/countries/', { page: 1, search: term, is_active: 'true' })
      .pipe(
        map((res) => res.results ?? []),
        tap((rows) => this.remember(rows)),
      );

  readonly allowedSearch = createServerSearch<CountryRow>({
    fetch: this.fetch,
    toOption: (row) => ({ value: String(row.id), label: row.name }),
    selected: () => this.value().allowed,
  });
  readonly disallowedSearch = createServerSearch<CountryRow>({
    fetch: this.fetch,
    toOption: (row) => ({ value: String(row.id), label: row.name }),
    selected: () => this.value().disallowed,
  });

  constructor() {
    this.allowedSearch.search('');
    this.disallowedSearch.search('');
  }

  setAllowed(ids: string[] | null): void {
    const allowed = ids ?? [];
    this.value.update((v) => ({ allowed, disallowed: v.disallowed.filter((id) => !allowed.includes(id)) }));
  }

  setDisallowed(ids: string[] | null): void {
    const disallowed = ids ?? [];
    this.value.update((v) => ({ allowed: v.allowed.filter((id) => !disallowed.includes(id)), disallowed }));
  }

  private remember(rows: CountryRow[]): void {
    const seen = new Set(this.countries().map((c) => c.id));
    const fresh = rows
      .filter((r) => !seen.has(String(r.id)))
      .map((r) => ({ id: String(r.id), code: r.code ?? '', name: r.name }));
    if (fresh.length) this.countries.set([...this.countries(), ...fresh]);
  }
}
